import { FC } from 'react';
import { IonButton, IonSpinner } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { RecordingState } from './RecordingState';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { CareMapState } from '../caremap/CareMapState';
import { ICareMap } from '../caremap/ICareMap';
import { DuoClipboardListIcon, DuoHandDotsIcon, DuoPrescriptionBottleIcon, MicrophoneIcon } from '../theme/Icons';
import { Card } from '../components/Card';
import { MemberAvatar } from '../household/MemberAvatar';

export interface IRecordingContext {
    type?: 'at-appointment' | 'post-appointment';
    label: string;
    consentHeader: string;
    consentMessage: string;
}

const CATEGORY_PILLS = [
    { label: 'Diagnoses', Icon: DuoClipboardListIcon },
    { label: 'Medications', Icon: DuoPrescriptionBottleIcon },
    { label: 'Allergies', Icon: DuoHandDotsIcon }
];

const useStyles = createUseStyles({
    container: {
    },
    cardContent: {
        padding: '24px 24px 28px'
    },
    beaHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 16
    },
    beaAvatar: {
        width: 36,
        height: 36,
        borderRadius: '50%',
        objectFit: 'cover'
    },
    beaName: {
        fontSize: 16,
        fontWeight: 600,
        color: 'var(--ion-color-dark)'
    },
    body: {
        fontSize: 15,
        lineHeight: 1.5,
        color: '#555',
        marginBottom: 20
    },
    pillsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 24
    },
    pill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        fontWeight: 500,
        padding: '5px 12px',
        borderRadius: 20,
        color: 'var(--ion-color-primary)',
        backgroundColor: 'rgba(var(--ion-color-primary-rgb), 0.1)',
        border: '1px solid rgba(var(--ion-color-primary-rgb), 0.25)'
    },
    recordButton: {
        fontWeight: '600',
        fontSize: '16px',
        height: '52px'
    },
    testimonial: {
        textAlign: 'center',
        padding: '16px 24px 20px'
    },
    quote: {
        fontSize: 15,
        lineHeight: 1.6,
        fontStyle: 'italic',
        color: '#888'
    },
    attribution: {
        fontSize: 14,
        color: '#aaa',
        marginTop: 12
    },
    viewerContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minHeight: 380,
        maxWidth: 340,
        margin: '0 auto',
        padding: '32px 24px 40px'
    },
    hiveRing: {
        display: 'inline-flex',
        padding: 3,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, var(--ion-color-warning) 0%, var(--ion-color-secondary) 100%)',
        boxShadow: '0 8px 20px rgba(238, 108, 78, 0.18)',
        marginBottom: 18
    },
    hiveRingInner: {
        display: 'inline-flex',
        padding: 3,
        borderRadius: '50%',
        background: '#fff'
    },
    viewerHeading: {
        fontSize: 22,
        fontWeight: 700,
        lineHeight: 1.25,
        color: 'var(--ion-color-dark)',
        marginBottom: 10
    },
    viewerPill: {
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'var(--ion-color-primary)',
        backgroundColor: 'rgba(var(--ion-color-primary-rgb), 0.09)',
        padding: '5px 12px',
        borderRadius: 999,
        marginBottom: 16
    },
    viewerMessage: {
        fontSize: 15,
        lineHeight: 1.55,
        color: '#5a6b73',
        maxWidth: 300,
        marginBottom: 26
    },
    viewerAddButton: {
        fontWeight: '600',
        height: '48px',
        minWidth: 210,
        '--border-radius': '12px'
    },
    viewerSecondary: {
        fontSize: 14,
        color: '#8a9aa0',
        marginTop: 18
    },
    viewerLink: {
        color: 'var(--ion-color-primary)',
        cursor: 'pointer',
        fontWeight: 600,
        textDecoration: 'underline'
    }
});

const ViewOnlyEmptyState: FC<{ careMap: ICareMap }> = ({ careMap }) => {
    const classes = useStyles();
    const history = useInstance(HistoryViewModel);
    const careMapState = useInstance(CareMapState);
    const patientFirstName = careMapState.getPatientFirstName(careMap);
    const ownerName = careMap.ownerName;

    const goToProfile = () => {
        history.push(`/care/${careMap.careMapId}`);
    };

    const addRecipient = () => {
        history.push('/q/new-care-recipient');
    };

    return (
        <div className={classes.viewerContainer}>
            <div className={classes.hiveRing}>
                <div className={classes.hiveRingInner}>
                    <MemberAvatar
                        member={{ photo: careMap.patient?.photo, avatar: careMap.patient?.avatar, color: careMap.patient?.color }}
                        size={76}
                    />
                </div>
            </div>
            <div className={classes.viewerHeading}>
                Welcome to <span className="mp-mask">{patientFirstName}</span>'s Hive
            </div>
            <div className={classes.viewerPill}>View-only access</div>
            <div className={classes.viewerMessage}>
                Stay in the loop and follow <span className="mp-mask">{patientFirstName}</span>'s care at a glance.{' '}
                {ownerName
                    ? <>To add notes or details, ask <span className="mp-mask">{ownerName}</span> for access.</>
                    : 'To add notes or details, ask a Co-Owner for access.'}
            </div>
            <IonButton
                color="primary"
                className={classes.viewerAddButton}
                onClick={goToProfile}
            >
                <span className="mp-mask">{`Go to ${patientFirstName}'s Profile`}</span>
            </IonButton>
            <div className={classes.viewerSecondary}>
                <div>Caring for someone else?</div>
                <span className={classes.viewerLink} onClick={addRecipient}>Add a care recipient</span>
            </div>
        </div>
    );
};

export const RecordingEmptyState: FC = () => {
    const classes = useStyles();
    const careMapState = useInstance(CareMapState);
    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);
    const careMap = careMapState.getMostRecentCaremap();

    // View-only members can't record; show a dedicated view-only home instead of the intro card.
    if (careMap && !careMap.links?.self?.actions.includes('put')) {
        return <ViewOnlyEmptyState careMap={careMap} />;
    }

    // Once there's a named care recipient, Bea asks about them by name rather than offering to
    // set up a profile, and the category pills go: we can't know whether a manually added
    // recipient already has those details. Falls back to the last name so a recipient entered
    // with only a surname still counts as named here, matching what the recorder already treats
    // as an established profile.
    const patientName = careMapState.getPatientFirstName(careMap) || careMapState.getPatientLastName(careMap);

    const introMessage = !patientName
        ? <>Tell me about who you're caring for and I'll set up their profile for you!</>
        : careMapState.isUserThePatient()
            ? <>Tell me more about how you're doing. I'll help keep track of things along the way!</>
            : <>Tell me more about <span className="mp-mask">{patientName}</span> and how they're doing. I'll help keep track of things along the way!</>;

    const handleRecord = () => {
        // No summaryPrompt: the recorder decides whether this is an intro recording. Forcing
        // 'intro' here would re-run profile extraction for a recipient who already has one.
        recordingState.triggerRecording({ initiatedFrom: 'Intro Card' });
    };

    return (
        <div className={classes.container}>
            <Card>
                <div className={classes.cardContent}>
                <div className={classes.beaHeader}>
                    <img
                        src="/assets/images/help/helper-bea.png"
                        alt="Bea"
                        className={classes.beaAvatar}
                    />
                    <span className={classes.beaName}>Bea</span>
                </div>
                <div className={classes.body} data-id="intro-card-body">
                    {introMessage}
                </div>
                {!patientName && (
                    <div className={classes.pillsRow} data-id="intro-card-pills">
                        {CATEGORY_PILLS.map(({ label, Icon }) => (
                            <span key={label} className={classes.pill}>
                                <Icon color="primary" customSize={14} />
                                {label}
                            </span>
                        ))}
                    </div>
                )}
                <IonButton
                    expand="block"
                    color="primary"
                    className={classes.recordButton}
                    disabled={recordingState.isStartingRecording()}
                    onClick={handleRecord}
                    data-id="intro-record-button"
                >
                    {recordingState.isStartingRecording() ? (
                        <IonSpinner name="dots" style={{height: 20}} />
                    ) : (
                        <>
                            <MicrophoneIcon color="light" customSize={20} />
                            <span style={{marginLeft: 8}}>Record</span>
                        </>
                    )}
                </IonButton>
                </div>
            </Card>

            <div className={classes.testimonial}>
                <div className={classes.quote}>
                    &ldquo;I just started talking about my mom and
                    in two minutes, Bea had it all captured.
                    I didn't even realize how much
                    I was carrying around in my head.&rdquo;
                </div>
                <div className={classes.attribution}>
                    &mdash; Sarah, caring for her mother
                </div>
            </div>
        </div>
    );
};
